<template>
  <ValidationObserver
    class="needs-validation form-custom-format"
    :class="{ 'was-validated': validated }"
    tag="form"
    ref="observer"
    v-slot="{ passes }"
    @submit.prevent="onSubmitForm"
  >
    <el-dialog
      :modal="true"
      ref="window"
      :fullscreen="true"
      :title="dialogTitle"
      :visible.sync="syncModal"
      :close-on-click-modal="false"
      :modal-append-to-body="true"
      :append-to-body="true"
      class="text-left bulk-update-qty-dialog"
      :formStatus="formStatus"
      @dialog-state="dialogState"
      :group="group"
      :showClose="!processing"
    >

      <template v-if="formStatus === 'action'">

       <el-row :gutter="20">
          <el-col :span="12" :offset="6" class="mt-4 mb-2">
            <el-radio-group v-model="bulkAction" size="medium" class="w-100">
                <el-radio label="create" border class="m-1">Create Items</el-radio>
                <el-radio label="update" border class="m-1">Update Items</el-radio>
            </el-radio-group>
            </el-col>
        </el-row>

           
      </template>

      <template v-else-if="bulkAction === 'update' && formStatus === 'select'">
        <el-row :gutter="20">
          <el-col :span="12" :offset="6" class="mt-4 mb-2">
            <el-checkbox
              :indeterminate="isIndeterminate"
              v-model="checkAll"
              @change="handleCheckAllChange"
              border
              size="medium"
            >
              CHECK ALL
            </el-checkbox>
          </el-col>
          <el-checkbox-group
            v-model="updateCheckedOptions"
            @change="handleCheckedOptionsChange"
          >
            <el-col
              :span="12"
              :offset="6"
              v-for="obj in updateCheckboxOptions"
              :key="obj.value"
              class="mt-1"
            >
              <el-checkbox
                :label="obj.value"
                :key="obj.value"
                border
                size="medium"
              >
                {{ obj.label.toUpperCase() }}
              </el-checkbox>
            </el-col>
          </el-checkbox-group>
        </el-row>
      </template>

      <!-- step 2/3: spreadsheet (used for both create and update) -->
      <div v-show="formStatus === 'create'" ref="spreadsheet"></div>

      <template #footer>
        <div class="d-flex">
          <!-- loading -->
          <template v-if="formLoading">
            <mdb-btn color="elegant" size="sm" disabled>
              <span
                class="spinner-border spinner-border-sm"
                role="status"
                aria-hidden="true"
              ></span>
              Loading...
            </mdb-btn>
          </template>

          <template v-else>
            <!-- footer when choosing action -->
            <template v-if="formStatus === 'action'">
              <mdb-btn
                color="elegant"
                size="sm"
                :disabled="!bulkAction"
                @click="onActionNext"
              >
                <i class="el-icon-right text-white cursor-pointer"></i> Next
              </mdb-btn>
            </template>

            <!-- footer when choosing columns for update -->
            <template v-else-if="bulkAction === 'update' && formStatus === 'select'">
              <template v-if="enableButton">
                <mdb-btn color="elegant" size="sm" disabled>
                  <span
                    class="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Please wait...Getting dependencies from server
                </mdb-btn>
              </template>
              <template v-else>
                <mdb-btn
                  color="elegant"
                  size="sm"
                  @click="onUpdateColumnsNext"
                  :disabled="!checkedOptionsComputed"
                >
                  <i class="el-icon-right text-white cursor-pointer"></i> Next
                </mdb-btn>
              </template>
              <mdb-btn color="grey" size="sm" @click="onBackToAction">
                <i class="el-icon-back text-white cursor-pointer"></i> Back
              </mdb-btn>
            </template>

            <!-- footer when showing spreadsheet (create or update) -->
            <template v-else-if="formStatus === 'create'">
              <template v-if="processing">
                <mdb-btn color="elegant" size="sm" disabled>
                  <span
                    class="spinner-border spinner-border-sm"
                    role="status"
                    aria-hidden="true"
                  ></span>
                  Processing...
                </mdb-btn>
              </template>
              <template v-else>
                <mdb-btn color="elegant" size="sm" @click="passes(onSubmitForm)">
                  <i class="el-icon-check text-white cursor-pointer"></i> Save
                </mdb-btn>
              </template>

              <mdb-btn color="grey" size="sm" @click="passes(clearForm)">
                <i class="el-icon-copy-document text-white cursor-pointer"></i> Clear
              </mdb-btn>

              <mdb-btn color="grey" size="sm" @click="passes(onSubmitCopyTemplate)">
                <i class="el-icon-copy-document text-white cursor-pointer"></i>
                Copy Template
              </mdb-btn>

              <!-- only update has back to columns -->
              <mdb-btn
                v-if="bulkAction === 'update'"
                color="grey"
                size="sm"
                @click="onUpdateBack"
              >
                <i class="el-icon-back text-white cursor-pointer"></i> Back
              </mdb-btn>
            </template>

            <!-- close is always visible -->
            <mdb-btn color="grey" size="sm" @click="closeDialog">
              <i class="el-icon-close text-white cursor-pointer"></i> Close
            </mdb-btn>
          </template>
        </div>
      </template>
    </el-dialog>
  </ValidationObserver>
</template>

<script>
import { mdbBtn } from "mdbvue";
import { ValidationObserver } from "vee-validate";
import { mapActions, mapGetters } from "vuex";
import jspreadsheet from "../../jsexcel/jexcel";
import { MessageBox, Message } from "element-ui";
import { ApiHttpService } from "../../services";

/* brand filter based on parent group */
/* product category filter based on product department */
/* collections belong to a brand - the BRAND cell on this row decides the list */
const dropdownCollectionFilter = function (instance, cell, c, r, source) {
  const grid = instance.jexcel || instance;
  const columns = (grid.options && grid.options.columns) || [];
  const brandColumn = columns.findIndex((column) => column.name === "brand");

  /* BRAND was not among the columns the user picked, so there is nothing to
     narrow by and every collection stays available. */
  if (brandColumn < 0) {
    return source;
  }

  const brand = grid.getValueFromCoords(brandColumn, r);

  if (brand === "" || brand === null || brand === undefined) {
    return source;
  }

  return source.filter((item) => String(item.brand_id) === String(brand), []);
};

const dropdownProductCategoryFilter = function (instance, cell, c, r, source) {
  const grid = instance.jexcel || instance;
  const columns = (grid.options && grid.options.columns) || [];
  const departmentColumn = columns.findIndex(
    (column) => column.name === "product_department_id"
  );

  if (departmentColumn < 0) {
    return source;
  }

  const departmentId = grid.getValueFromCoords(departmentColumn, r);
  if (departmentId === "" || departmentId === null || departmentId === undefined) {
    return source;
  }

  return source.filter(
    (item) => String(item.department_id) === String(departmentId),
    []
  );
};

/* product type filter based on product category */
const dropdownProductTypeFilter = function (instance, cell, c, r, source) {
  const grid = instance.jexcel || instance;
  const columns = (grid.options && grid.options.columns) || [];
  const categoryColumn = columns.findIndex(
    (column) => column.name === "product_category_id"
  );

  if (categoryColumn < 0) {
    return source;
  }

  const categoryId = grid.getValueFromCoords(categoryColumn, r);
  if (categoryId === "" || categoryId === null || categoryId === undefined) {
    return source;
  }

  return source.filter(
    (item) => String(item.category_id) === String(categoryId),
    []
  );
};

/* default uom options filtered by selected group */
const dropdownDefaultUomFilter = function (instance, cell, c, r, source) {
  const groupValue = instance.jexcel.getValueFromCoords(c - 1, r);
  if (!groupValue) {
    return [];
  }
  const result = source.filter((item) => {
    return Array.isArray(item.uomGroups) && item.uomGroups.includes(groupValue);
  });
  return result;
};

export default {
  name: "BulkItemCreateOrUpdate",

  components: {
    mdbBtn,
    ValidationObserver,
  },

  props: {
    group: {
      type: Number,
      default: 1,
    },
  },

  inject: ["brandProvider", "categoryProvider", "areaProvider", "collectionProvider"],

  data() {
    const self = this;
    return {
      syncModal: false,
      validated: false,

      // wizard status: "action" -> "select" (update only) -> "create" (spreadsheet)
      formStatus: "action",
      bulkAction: "create", 

      form: null,
      formData: null,
      formDataOrginal: null,
      formLoading: false,
      processing: false,
      showError: false,
      productDepartments: [],
      productCategories: [],
      productTypes: [],

      spreadsheet: null,
      exportTableOptions: {
        download: false,
        allowDeleteColumn: false,
        allowRenameColumn: false,
        allowAddColumn: false,
        allowInsertColumn: false,
        defaultColAlign: "left",
        csvHeader: true,
        tableWidth: "100%",
        tableHeight: "calc(100vh - 85px)",
        tableOverflow: true,
        data: [[]],
        minDimensions: [3, 50],
        onCopy: null,
        selectionCopy: false,
        allowCopy: true,
        columns: [
          {
            type: "text",
            width: "200px",
            title: "SERIES",
            name: "series",
            readOnly: false,
            primaryKey: false,
          },
          {
            type: "text",
            width: "340px",
            title: "RESPONSE",
            name: "message",
          },
        ],
        contextMenu: function () {
          return false;
        },
        onchange: function (instance, domElmentCell, column, row) {
          self.recalculateDimension(instance, column, row);
        },
      },

      // column selection for UPDATE
      isIndeterminate: false,
      checkAll: true,
      updateCheckedOptions: [
        "name",
        "dimension",
        "description",
        "product_type",
        "product_department_id",
        "product_category_id",
        "product_type_id",
        "brand",
        "collection",
        "status",
        "item_type",
        "website",
        "sale_price",
        "PurchaseUomDimension",
        "SalesUomDimension",
        "vendor_code",
        "SuppCatNum",
        "old_code",
        "ForeignName",
        "country",
        "UoMGroupEntry",
        "DefaultUomEntry",
        "CountryOfOriginEn",
        "CountryOfOriginAr",
        "inventory",
        "sell",
        "purchase",
      ],
      updateCheckboxOptions: [
        { label: "ITEM NAME", value: "name", width: "200px", dropdown: false, type: "text" },
        { label: "DIMENSION", value: "dimension", width: "100px", dropdown: false, type: "text" },
        { label: "DESCRIPTION", value: "description", width: "100px", dropdown: false, type: "text" },
        {
          label: "PRODUCT CLASSIFICATION",
          value: "product_type",
          width: "200px",
          dropdown: true,
          type: "dropdown",
          source: [
            { name: "PRODUCT", id: "product" },
            { name: "SET", id: "set" },
            { name: "PART", id: "part" },
          ],
        },
        {
          label: "PRODUCT DEPARTMENT",
          value: "product_department_id",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: "productDepartmentComputed",
        },
        {
          label: "PRODUCT CATEGORY",
          value: "product_category_id",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: "productCategoryComputed",
        },
        {
          label: "PRODUCT TYPE",
          value: "product_type_id",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: "productTypeComputed",
        },
        { label: "BRAND", value: "brand", width: "100px", dropdown: true, type: "dropdown", source: "brandComputed" },
        { label: "COLLECTION", value: "collection", width: "100px", dropdown: true, type: "dropdown", source: "collectionComputed" },
        {
          label: "ITEM STATUS",
          value: "status",
          width: "100px",
          dropdown: true,
          type: "dropdown",
          source: [
            { name: "Archive", id: "Archive" },
            { name: "Active", id: "Active" },
            { name: "Discontinued", id: "Discontinued" },
          ],
        },
        {
          label: "ITEM TYPE",
          value: "item_type",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: [
            { name: "Stock", id: "Stock" },
            { name: "Service", id: "Service" },
          ],
        },
        {
          label: "WEB LIVE STATUS",
          value: "website",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
        {
          label: "SALE PRICE",
          value: "sale_price",
          width: "150px",
          dropdown: false,
          type: "numeric",
        },
        {
          label: "PURCHASE UOM DIMENSION",
          value: "PurchaseUomDimension",
          width: "150px",
          dropdown: false,
          type: "text",
        },
        {
          label: "SALES UOM DIMENSION",
          value: "SalesUomDimension",
          width: "150px",
          dropdown: false,
          type: "text",
        },
        { label: "VENDOR CODE", value: "vendor_code", width: "150px", dropdown: false, type: "text" },
        { label: "SUPPLIER CODE", value: "SuppCatNum", width: "150px", type: "text" },
        { label: "OLD CODE", value: "old_code", width: "150px", type: "text" },
        { label: "FOREIGN NAME", value: "ForeignName", width: "150px", type: "text" },
        {
          label: "COUNTRY",
          value: "country",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: "countriesComputed",
        },
        {
          label: "UoM GROUP",
          value: "UoMGroupEntry",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: "unitOfMeasurementGroupComputed",
        },
         {
          label: "DEFAULT UOM",
          value: "DefaultUomEntry",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: "defaultUnitOfMeasurementComputed",
        },
        {
          label: "COUNTRY OF ORIGIN (EN)",
          value: "CountryOfOriginEn",
          width: "190px",
          type: "text",
        },
        {
          label: "COUNTRY OF ORIGIN (AR)",
          value: "CountryOfOriginAr",
          width: "190px",
          type: "text",
        },
        {
          label: "INVENTORY ITEM",
          value: "inventory",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
        {
          label: "SALES ITEM",
          value: "sell",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
        {
          label: "PURCHASE ITEM",
          value: "purchase",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
      ],

      // fixed cols for CREATE (no UI selection)
      createCheckedOptions: ["product_type", "product_department_id", "product_category_id", "product_type_id", "brand", "collection"],
      createCheckboxOptions: [
        {
          label: "PRODUCT CLASSIFICATION",
          value: "product_type",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: [
            { name: "PRODUCT", id: "product" },
            { name: "SET", id: "set" },
            { name: "PART", id: "part" },
          ],
        },
        {
          label: "DEPARTMENT",
          value: "product_department_id",
          width: "100px",
          dropdown: true,
          type: "dropdown",
          source: "productDepartmentComputed",
        },
        {
          label: "CATEGORY",
          value: "product_category_id",
          width: "100px",
          dropdown: true,
          type: "dropdown",
          source: "productCategoryComputed",
        },
        {
          label: "PRODUCT TYPE",
          value: "product_type_id",
          width: "100px",
          dropdown: true,
          type: "dropdown",
          source: "productTypeComputed",
        },
        {
          label: "BRAND",
          value: "brand",
          width: "100px",
          dropdown: true,
          type: "dropdown",
          source: "brandComputed",
        },
        {
          label: "COLLECTION",
          value: "collection",
          width: "100px",
          dropdown: true,
          type: "dropdown",
          source: "collectionComputed",
        },
        {
          label: "ITEM STATUS",
          value: "status",
          width: "100px",
          dropdown: true,
          type: "dropdown",
          source: [
            { name: "Archive", id: "Archive" },
            { name: "Active", id: "Active" },
            { name: "Discontinued", id: "Discontinued" },
          ],
        },
        {
          label: "ITEM TYPE",
          value: "item_type",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: [
            { name: "Stock", id: "Stock" },
            { name: "Service", id: "Service" },
          ],
        },
        {
          label: "WEB LIVE STATUS",
          value: "website",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
        {
          label: "PRICE",
          value: "sale_price",
          width: "150px",
          dropdown: false,
          type: "numeric",
        },
      ],

      postedData: [],
      postCounter: 0,
      dataLength: 0,
    };
  },

  created() {
    if (this.formObject) {
      this.formData = JSON.parse(JSON.stringify(this.formObject));
      this.formDataOrginal = JSON.parse(JSON.stringify(this.formObject));
    }
  },

  mounted() {
    // load shared lookups
    this.getUnitOfMeasurements();
    this.getUnitOfMeasurementGroups();
    this.getCountries && this.getCountries();
    this.loadProductCategorization();
  },

  methods: {
    // store actions
    ...mapActions("InventoryItemMasterModule", ["storeRecord", "bulkItemUpdate"]),
    ...mapActions("UnitOfMeasurementModuleV2", ["getUnitOfMeasurementGroups", "getUnitOfMeasurements"]),
    ...mapActions("CountryModule", ["getCountries"]),

    normalizeProductClassification(value) {
      return value ? String(value).trim().toLowerCase() : null;
    },

    /* helper to open modal from parent */
    openDialog() {
      this.syncModal = true;
      this.resetForm();
      this.bulkAction = null;
      this.formStatus = "action";
    },

    closeDialog() {
      this.syncModal = false;
      this.resetForm();
    },

    resetForm() {
      this.form = this.formVar();
      if (this.$refs.observer) {
        requestAnimationFrame(() => {
          this.$refs.observer.reset();
        });
      }
      this.validated = false;
      this.formLoading = false;
      this.processing = false;
      this.postedData = [];
      this.postCounter = 0;
      this.dataLength = 0;

      if (this.spreadsheet) {
        this.spreadsheet.destroy();
        this.spreadsheet = null;
      }
    },

    createPopupTable(exportTableOptions) {
      this.spreadsheet = jspreadsheet(this.$refs.spreadsheet, exportTableOptions);
      Object.assign(this, this.spreadsheet);
      this.spreadsheet.setData([]);
    },

    async recreateSpreadsheet() {
      if (!this.spreadsheet) return;
      await this.spreadsheet.destroy();
      this.spreadsheet = null;
    },

    buildExportOptions(columns) {
      const self = this;

      this.exportTableOptions = {
        download: false,
        allowDeleteColumn: false,
        allowRenameColumn: false,
        allowAddColumn: false,
        allowInsertColumn: false,
        defaultColAlign: "left",
        csvHeader: true,
        tableWidth: "100%",
        tableHeight: "calc(100vh - 85px)",
        tableOverflow: true,
        data: [[]],
        minDimensions: [columns.length || 3, 50],
        onCopy: null,
        selectionCopy: false,
        allowCopy: true,
        columns,
        contextMenu() {
          return false;
        },
        onchange(instance, domElmentCell, column, row) {
          self.recalculateDimension(instance, column, row);
        },
      };
    },

    optionsHandler() {
      // keep for compatibility with existing usage
    },

    dialogState(state) {
      this.optionsHandler();
      if (!state) {
        this.closeDialog();
      }
    },

    /* wizard buttons */

    onActionNext() {
      if (this.bulkAction === "create") {
        this.startCreateMode();
      } else if (this.bulkAction === "update") {
        this.startUpdateSelectionMode();
      }
    },

    startCreateMode() {
      this.resetForm();
      this.formStatus = "create";
      this.formLoading = true;
      this.loadProductCategorization().finally(() => {
        this.formLoading = false;
        this.buildCreateSpreadsheet();
      });
    },

    loadProductCategorization() {
      return Promise.all([
        ApiHttpService.getRequest("product-departments", { length: 1000 }),
        ApiHttpService.getRequest("category-master/all", { length: 1000 }),
        ApiHttpService.getRequest("product-types", { length: 1000 }),
      ]).then(([departmentsResponse, categoriesResponse, productTypesResponse]) => {
        this.productDepartments = departmentsResponse.data.data || departmentsResponse.data || [];
        this.productCategories = categoriesResponse.data.data || categoriesResponse.data || [];
        this.productTypes = productTypesResponse.data.data || productTypesResponse.data || [];
      });
    },

    startUpdateSelectionMode() {
      this.resetForm();
      this.formStatus = "select";
    },

    onUpdateColumnsNext() {
      this.formStatus = "create";
      this.buildUpdateSpreadsheet();
    },

    onBackToAction() {
      this.formStatus = "action";
      this.resetForm();
    },

    onUpdateBack() {
      this.formStatus = "select";
      this.recreateSpreadsheet();
    },

    onSubmitCopyTemplate() {
      const self = this;
      const options =
        this.bulkAction === "update"
          ? this.updateCheckboxOptions
          : this.createCheckboxOptions;
      const selectedValues =
        this.bulkAction === "update"
          ? this.updateCheckedOptions
          : this.createCheckedOptions;

      const selectedColumns = options.filter((item) => {
        return selectedValues.includes(item.value);
      }, []);

      let txt = "Series\t";
      if (selectedColumns.length > 0) {
        selectedColumns.forEach((element) => {
          txt += element.label + "\t";
        });
        txt += "\n";
      }

      const dummy = document.createElement("textarea");
      document.body.appendChild(dummy);
      dummy.setAttribute("id", "dummy_id");
      document.getElementById("dummy_id").value = txt;
      dummy.select();
      document.execCommand("copy");
      document.body.removeChild(dummy);

      Message.success({
        message: "Copied!",
      });
    },

    /* build spreadsheet for CREATE */

   /*  buildCreateSpreadsheet() {
      const self = this;

      const tableCols = this.createCheckboxOptions
        .filter((obj) => {
          return this.createCheckedOptions.includes(obj.value);
        }, [])
        .map((obj) => {
          const dropdownObject = {};
          const numericObject = {};

          if (obj.hasOwnProperty("dropdown") && obj.dropdown === true) {
            const fromComputed = [
              "brandComputed",
              "categoryComputed",
              "areaComputed",
              "groupComputed",
              "collectionComputed",
              "countriesComputed",
              "productDepartmentComputed",
              "productCategoryComputed",
              "productTypeComputed",
            ];
            dropdownObject.source = fromComputed.includes(obj.source)
              ? this[`${obj.source}`]
              : obj.source;
            dropdownObject.autocomplete = true;
            dropdownObject.multiple = false;

            if (obj.source === "collectionComputed") {
              dropdownObject.filter = dropdownCollectionFilter;
            }
            if (obj.source === "productCategoryComputed") {
              dropdownObject.filter = dropdownProductCategoryFilter;
            }
            if (obj.source === "productTypeComputed") {
              dropdownObject.filter = dropdownProductTypeFilter;
            }
          }

          if (obj.type === "numeric") {
            numericObject.mask = "#,##.00";
            numericObject.width = 100;
            numericObject.decimal = ".";
          }

          return {
            type: obj.type,
            width: obj.width,
            title: obj.label,
            name: obj.value,
            readOnly: false,
            ...dropdownObject,
            ...numericObject,
          };
        });

      const finalColumns = [
        {
          type: "text",
          width: "200px",
          title: "ITEM CODE",
          name: "series",
          readOnly: false,
          primaryKey: false,
        },
        {
          type: "text",
          width: "200px",
          title: "ITEM NAME",
          name: "name",
          readOnly: false,
          primaryKey: false,
        },
        {
          type: "text",
          width: "200px",
          title: "SUPPLIER CODE",
          name: "barcode",
          readOnly: false,
          primaryKey: false,
        },
        {
          type: "text",
          width: "200px",
          title: "OLD CODE",
          name: "old_code",
          readOnly: false,
          primaryKey: false,
        },
        ...tableCols,
        {
          type: "numeric",
          width: "100px",
          title: "PRICE",
          name: "sale_price",
          readOnly: false,
          primaryKey: false,
        },
        {
          type: "dropdown",
          width: "150px",
          title: "DIMENSION TYPE",
          name: "dimension_type",
          readOnly: false,
          primaryKey: false,
          source: [
            { name: "Size", id: "size" },
            { name: "LxWxH", id: "whd" },
          ],
        },
        { title: "LENGTH", name: "U_itm_length", width: "150px", dropdown: false, type: "text" },
        { title: "WIDTH", name: "U_itm_width", width: "150px", dropdown: false, type: "text" },
        { title: "HEIGHT", name: "U_itm_height", width: "150px", dropdown: false, type: "text" },
        {
          type: "text",
          width: "200px",
          title: "DIMENSION",
          name: "dimension",
          readOnly: false,
          primaryKey: false,
        },
        {
          type: "text",
          width: "400px",
          title: "DESCRIPTION",
          name: "description",
          readOnly: false,
          primaryKey: false,
        },
        {
          type: "dropdown",
          width: "150px",
          title: "ITEM STATUS",
          name: "status",
          readOnly: false,
          primaryKey: false,
          source: [
            { name: "Archive", id: "Archive" },
            { name: "Active", id: "Active" },
            { name: "Discontinued", id: "Discontinued" },
          ],
        },
        {
          type: "dropdown",
          width: "100px",
          title: "WEBLIVE STATUS",
          name: "website",
          readOnly: false,
          primaryKey: false,
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
        {
          type: "dropdown",
          width: "100px",
          title: "SALES ITEM",
          name: "sale_item",
          readOnly: false,
          primaryKey: false,
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
        {
          type: "dropdown",
          width: "100px",
          title: "PURCHASING ITEM",
          name: "purchase_item",
          readOnly: false,
          primaryKey: false,
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
        {
          type: "dropdown",
          width: "100px",
          title: "INVENTORY ITEM",
          name: "inventory_item",
          readOnly: false,
          primaryKey: false,
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
        {
          type: "text",
          width: "150px",
          title: "VENDOR CODE",
          name: "vendor_code",
          readOnly: false,
          primaryKey: false,
        },
        { title: "FOREIGN NAME", name: "ForeignName", width: "150px", type: "text" },
        {
          title: "COUNTRY",
          name: "country",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: self.countriesComputed,
        },
        {
          title: "UOM GROUP",
          name: "UoMGroupEntry",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: self.unitOfMeasurementGroupComputed,
        },
        {
          title: "DEFAULT UOM",
          name: "DefaultUomEntry",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: self.defaultUnitOfMeasurementComputed,
          filter: dropdownDefaultUomFilter,
        },
        {
          title: "COUNTRY OF ORIGIN (EN)",
          name: "CountryOfOriginEn",
          width: "190px",
          type: "text",
        },
        {
          title: "COUNTRY OF ORIGIN (AR)",
          name: "CountryOfOriginAr",
          width: "190px",
          type: "text",
        },
        {
          type: "text",
          width: "340px",
          title: "RESPONSE",
          name: "message",
          readOnly: false,
          primaryKey: false,
        },
      ];

      this.$nextTick(() => {
        this.exportTableOptions.tableWidth = "100%";
        this.exportTableOptions.columns = finalColumns;
        this.createPopupTable(this.exportTableOptions);
      });
    }, */

    buildCreateSpreadsheet() {
      const self = this;

      const tableCols = this.createCheckboxOptions
        .filter((obj) => {
          return this.createCheckedOptions.includes(obj.value);
        }, [])
        .map((obj) => {
          const dropdownObject = {};
          const numericObject = {};

          if (obj.hasOwnProperty("dropdown") && obj.dropdown === true) {
            const fromComputed = [
              "brandComputed",
              "categoryComputed",
              "areaComputed",
              "groupComputed",
              "collectionComputed",
              "countriesComputed",
              "productDepartmentComputed",
              "productCategoryComputed",
              "productTypeComputed",
              "unitOfMeasurementGroupComputed",
              "defaultUnitOfMeasurementComputed",
            ];
            dropdownObject.source = fromComputed.includes(obj.source)
              ? this[`${obj.source}`]
              : obj.source;
            dropdownObject.autocomplete = true;
            dropdownObject.multiple = false;

            if (obj.source === "collectionComputed") {
              dropdownObject.filter = dropdownCollectionFilter;
            }
            if (obj.source === "productCategoryComputed") {
              dropdownObject.filter = dropdownProductCategoryFilter;
            }
            if (obj.source === "productTypeComputed") {
              dropdownObject.filter = dropdownProductTypeFilter;
            }
            if(obj.source === "defaultUnitOfMeasurementComputed"){
              dropdownObject.filter = dropdownDefaultUomFilter;
            }

          }

          if (obj.type === "numeric") {
            numericObject.mask = "#,##.00";
            numericObject.width = 100;
            numericObject.decimal = ".";
          }

          return {
            type: obj.type,
            width: obj.width,
            title: obj.label,
            name: obj.value,
            readOnly: false,
            ...dropdownObject,
            ...numericObject,
          };
        });

      const finalColumns = [
        {
          type: "text",
          width: "200px",
          title: "ITEM CODE",
          name: "series",
          readOnly: false,
          primaryKey: false,
        },
        {
          type: "text",
          width: "200px",
          title: "ITEM NAME",
          name: "name",
          readOnly: false,
          primaryKey: false,
        },
        {
          type: "text",
          width: "200px",
          title: "SUPPLIER CODE",
          name: "barcode",
          readOnly: false,
          primaryKey: false,
        },
        {
          type: "text",
          width: "200px",
          title: "OLD CODE",
          name: "old_code",
          readOnly: false,
          primaryKey: false,
        },
        ...tableCols,
        {
          type: "numeric",
          width: "100px",
          title: "PRICE",
          name: "sale_price",
          readOnly: false,
          primaryKey: false,
        },
        {
          type: "dropdown",
          width: "150px",
          title: "DIMENSION TYPE",
          name: "dimension_type",
          readOnly: false,
          primaryKey: false,
          source: [
            { name: "Size", id: "size" },
            { name: "LxWxH", id: "whd" },
          ],
        },
        { title: "LENGTH", name: "U_itm_length", width: "150px", dropdown: false, type: "text" },
        { title: "WIDTH", name: "U_itm_width", width: "150px", dropdown: false, type: "text" },
        { title: "HEIGHT", name: "U_itm_height", width: "150px", dropdown: false, type: "text" },
        {
          type: "text",
          width: "200px",
          title: "DIMENSION",
          name: "dimension",
          readOnly: false,
          primaryKey: false,
        },
        {
          type: "text",
          width: "400px",
          title: "DESCRIPTION",
          name: "description",
          readOnly: false,
          primaryKey: false,
        },
        {
          type: "dropdown",
          width: "150px",
          title: "ITEM STATUS",
          name: "status",
          readOnly: false,
          primaryKey: false,
          source: [
            { name: "Archive", id: "Archive" },
            { name: "Active", id: "Active" },
            { name: "Discontinued", id: "Discontinued" },
          ],
        },
        {
          type: "dropdown",
          width: "100px",
          title: "WEBLIVE STATUS",
          name: "website",
          readOnly: false,
          primaryKey: false,
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
        {
          type: "dropdown",
          width: "100px",
          title: "SALES ITEM",
          name: "sale_item",
          readOnly: false,
          primaryKey: false,
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
        {
          type: "dropdown",
          width: "100px",
          title: "PURCHASING ITEM",
          name: "purchase_item",
          readOnly: false,
          primaryKey: false,
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
        {
          type: "dropdown",
          width: "100px",
          title: "INVENTORY ITEM",
          name: "inventory_item",
          readOnly: false,
          primaryKey: false,
          source: [
            { name: "Yes", id: "Yes" },
            { name: "No", id: "No" },
          ],
        },
        {
          type: "text",
          width: "150px",
          title: "VENDOR CODE",
          name: "vendor_code",
          readOnly: false,
          primaryKey: false,
        },
        { title: "FOREIGN NAME", name: "ForeignName", width: "150px", type: "text" },
        {
          title: "COUNTRY",
          name: "country",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: self.countriesComputed,
        },
        {
          title: "UoM GROUP",
          name: "UoMGroupEntry",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: self.unitOfMeasurementGroupComputed,
        },
        {
          title: "DEFAULT UOM",
          name: "DefaultUomEntry",
          width: "150px",
          dropdown: true,
          type: "dropdown",
          source: self.defaultUnitOfMeasurementComputed,
          filter: dropdownDefaultUomFilter,
        },
        {
          title: "COUNTRY OF ORIGIN (EN)",
          name: "CountryOfOriginEn",
          width: "190px",
          type: "text",
        },
        {
          title: "COUNTRY OF ORIGIN (AR)",
          name: "CountryOfOriginAr",
          width: "190px",
          type: "text",
        },
        {
          type: "text",
          width: "340px",
          title: "RESPONSE",
          name: "message",
          readOnly: false,
          primaryKey: false,
        },
      ];

      // rebuild options completely for this column set
      this.buildExportOptions(finalColumns);

      // destroy old sheet if any, then create a new one
      if (this.spreadsheet) {
        this.recreateSpreadsheet();
      }

      this.$nextTick(() => {
        this.createPopupTable(this.exportTableOptions);
      });
    },

    /* build spreadsheet for UPDATE */

    /* buildUpdateSpreadsheet() {
      const self = this;

      const tableCols = this.updateCheckboxOptions
        .filter((obj) => {
          return this.updateCheckedOptions.includes(obj.value);
        }, [])
        .map((obj) => {
          const dropdownObject = {};
          const numericObject = {};

          if (obj.hasOwnProperty("dropdown") && obj.dropdown === true) {
            const fromComputed = [
              "brandComputed",
              "categoryComputed",
              "areaComputed",
              "groupComputed",
              "collectionComputed",
              "countriesComputed",
              "unitOfMeasurementGroupComputed",
            ];
            dropdownObject.source = fromComputed.includes(obj.source)
              ? this[`${obj.source}`]
              : obj.source;
            dropdownObject.autocomplete = true;
            dropdownObject.multiple = false;

            if (obj.source === "collectionComputed") {
              dropdownObject.filter = dropdownCollectionFilter;
            }
          }

          if (obj.type === "numeric") {
            numericObject.mask = "#,##.00";
            numericObject.width = 100;
            numericObject.decimal = ".";
          }

          return {
            type: obj.type,
            width: obj.width,
            title: obj.label,
            name: obj.value,
            readOnly: false,
            ...dropdownObject,
            ...numericObject,
          };
        });

      const finalOrderColumns = [];

      tableCols.forEach((col) => {
        const colData = col;

        if (colData.name === "dimension") {
          const dimensionColumns = [
            {
              title: "DIMENSION TYPE",
              name: "dimension_type",
              width: "150px",
              dropdown: true,
              type: "dropdown",
              source: [
                { name: "Size", id: "size" },
                { name: "LxWxH", id: "whd" },
              ],
            },
            { title: "LENGTH", name: "U_itm_length", width: "150px", dropdown: false, type: "text" },
            { title: "WIDTH", name: "U_itm_width", width: "150px", dropdown: false, type: "text" },
            { title: "HEIGHT", name: "U_itm_height", width: "150px", dropdown: false, type: "text" },
            {
              title: "DIMENSION",
              name: "dimension",
              width: "150px",
              dropdown: false,
              type: "text",
              readOnly: true,
            },
          ];
          finalOrderColumns.push(...dimensionColumns);
          return;
        }

        if (colData.name === "SalesUomDimension") {
          const salesColumns = [
            { title: "SALES LENGTH", name: "SalesUnitLength", width: "150px", dropdown: false, type: "numeric" },
            { title: "SALES WIDTH", name: "SalesUnitWidth", width: "150px", dropdown: false, type: "numeric" },
            { title: "SALES HEIGHT", name: "SalesUnitHeight", width: "150px", dropdown: false, type: "numeric" },
            {
              title: "SALES VOLUME",
              name: "SalesUnitVolume",
              width: "150px",
              dropdown: false,
              type: "numeric",
              readOnly: true,
            },
            { title: "SALES WEIGHT", name: "SalesUnitWeight", width: "150px", dropdown: false, type: "numeric" },
          ];
          finalOrderColumns.push(...salesColumns);
          return;
        }

        if (colData.name === "PurchaseUomDimension") {
          const purchaseColumns = [
            { title: "PURCHASE LENGTH", name: "PurchaseUnitLength", width: "150px", dropdown: false, type: "numeric" },
            { title: "PURCHASE WIDTH", name: "PurchaseUnitWidth", width: "150px", dropdown: false, type: "numeric" },
            { title: "PURCHASE HEIGHT", name: "PurchaseUnitHeight", width: "150px", dropdown: false, type: "numeric" },
            {
              title: "PURCHASE VOLUME",
              name: "PurchaseUnitVolume",
              width: "150px",
              dropdown: false,
              type: "numeric",
              readOnly: true,
            },
            { title: "PURCHASE WEIGHT", name: "PurchaseUnitWeight", width: "150px", dropdown: false, type: "numeric" },
          ];
          finalOrderColumns.push(...purchaseColumns);
          return;
        }

        finalOrderColumns.push(colData);
      });

      const finalColumns = [
        {
          type: "text",
          width: "200px",
          title: "SERIES",
          name: "series",
          readOnly: false,
          primaryKey: false,
        },
        ...finalOrderColumns,
        {
          type: "text",
          width: "340px",
          title: "RESPONSE",
          name: "message",
          readOnly: false,
        },
      ];

      if (finalColumns.filter((i) => i.name === "message").length > 0) {
        this.exportTableOptions.minDimensions = [finalColumns.length, 50];
      }

      if (this.spreadsheet) {
        this.recreateSpreadsheet();
      }

      this.$nextTick(() => {
        this.exportTableOptions.tableWidth = "100%";
        this.exportTableOptions.columns = finalColumns;
        this.createPopupTable(this.exportTableOptions);
      });
    }, */

     buildUpdateSpreadsheet() {
      const self = this;

      const tableCols = this.updateCheckboxOptions
        .filter((obj) => {
          return this.updateCheckedOptions.includes(obj.value);
        }, [])
        .map((obj) => {
          const dropdownObject = {};
          const numericObject = {};

          if (obj.hasOwnProperty("dropdown") && obj.dropdown === true) {
            const fromComputed = [
              "brandComputed",
              "categoryComputed",
              "areaComputed",
              "groupComputed",
              "collectionComputed",
              "countriesComputed",
              "productDepartmentComputed",
              "productCategoryComputed",
              "productTypeComputed",
              "unitOfMeasurementGroupComputed",
              "defaultUnitOfMeasurementComputed",
            ];
            dropdownObject.source = fromComputed.includes(obj.source)
              ? this[`${obj.source}`]
              : obj.source;
            dropdownObject.autocomplete = true;
            dropdownObject.multiple = false;

            if (obj.source === "collectionComputed") {
              dropdownObject.filter = dropdownCollectionFilter;
            }
            if (obj.source === "productCategoryComputed") {
              dropdownObject.filter = dropdownProductCategoryFilter;
            }
            if (obj.source === "productTypeComputed") {
              dropdownObject.filter = dropdownProductTypeFilter;
            }
          }

          if (obj.type === "numeric") {
            numericObject.mask = "#,##.00";
            numericObject.width = 100;
            numericObject.decimal = ".";
          }

          return {
            type: obj.type,
            width: obj.width,
            title: obj.label,
            name: obj.value,
            readOnly: false,
            ...dropdownObject,
            ...numericObject,
          };
        });

      const finalOrderColumns = [];

      tableCols.forEach((col) => {
        const colData = col;

        if (colData.name === "dimension") {
          const dimensionColumns = [
            {
              title: "DIMENSION TYPE",
              name: "dimension_type",
              width: "150px",
              dropdown: true,
              type: "dropdown",
              source: [
                { name: "Size", id: "size" },
                { name: "LxWxH", id: "whd" },
              ],
            },
            { title: "LENGTH", name: "U_itm_length", width: "150px", dropdown: false, type: "text" },
            { title: "WIDTH", name: "U_itm_width", width: "150px", dropdown: false, type: "text" },
            { title: "HEIGHT", name: "U_itm_height", width: "150px", dropdown: false, type: "text" },
            {
              title: "DIMENSION",
              name: "dimension",
              width: "150px",
              dropdown: false,
              type: "text",
              readOnly: true,
            },
          ];
          finalOrderColumns.push(...dimensionColumns);
          return;
        }

        if (colData.name === "SalesUomDimension") {
          const salesColumns = [
            { title: "SALES LENGTH", name: "SalesUnitLength", width: "150px", dropdown: false, type: "numeric" },
            { title: "SALES WIDTH", name: "SalesUnitWidth", width: "150px", dropdown: false, type: "numeric" },
            { title: "SALES HEIGHT", name: "SalesUnitHeight", width: "150px", dropdown: false, type: "numeric" },
            {
              title: "SALES VOLUME",
              name: "SalesUnitVolume",
              width: "150px",
              dropdown: false,
              type: "numeric",
              readOnly: true,
            },
            { title: "SALES WEIGHT", name: "SalesUnitWeight", width: "150px", dropdown: false, type: "numeric" },
          ];
          finalOrderColumns.push(...salesColumns);
          return;
        }

        if (colData.name === "PurchaseUomDimension") {
          const purchaseColumns = [
            { title: "PURCHASE LENGTH", name: "PurchaseUnitLength", width: "150px", dropdown: false, type: "numeric" },
            { title: "PURCHASE WIDTH", name: "PurchaseUnitWidth", width: "150px", dropdown: false, type: "numeric" },
            { title: "PURCHASE HEIGHT", name: "PurchaseUnitHeight", width: "150px", dropdown: false, type: "numeric" },
            {
              title: "PURCHASE VOLUME",
              name: "PurchaseUnitVolume",
              width: "150px",
              dropdown: false,
              type: "numeric",
              readOnly: true,
            },
            { title: "PURCHASE WEIGHT", name: "PurchaseUnitWeight", width: "150px", dropdown: false, type: "numeric" },
          ];
          finalOrderColumns.push(...purchaseColumns);
          return;
        }

        finalOrderColumns.push(colData);
      });

      const finalColumns = [
        {
          type: "text",
          width: "200px",
          title: "SERIES",
          name: "series",
          readOnly: false,
          primaryKey: false,
        },
        ...finalOrderColumns,
        {
          type: "text",
          width: "340px",
          title: "RESPONSE",
          name: "message",
          readOnly: false,
        },
      ];

      // completely rebuild options for this column set
      this.buildExportOptions(finalColumns);

      if (this.spreadsheet) {
        this.recreateSpreadsheet();
      }

      this.$nextTick(() => {
        this.createPopupTable(this.exportTableOptions);
      });
    },

    onSubmitBack() {
      // not used directly in this merged flow; kept to match old API if needed
      if (this.bulkAction === "update") {
        this.onUpdateBack();
      } else {
        this.onBackToAction();
      }
    },

    onSubmitForm() {
      if (this.bulkAction === "create") {
        this.submitCreateForm();
      } else if (this.bulkAction === "update") {
        this.submitUpdateForm();
      }
    },

    /* CREATE save logic */

    submitCreateForm() {
      const self = this;

      if (this.processing) {
        return false;
      }

      const tableData = this.spreadsheet.getJson();

      MessageBox.confirm(
        "Warning: Are you sure you want to submit the data. Do you want to continue?",
        "Warning",
        {
          confirmButtonText: "OK",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      )
        .then(() => {
          if (tableData.length > 0) {
            this.postCounter = 0;
            this.dataLength = tableData.filter((item) => item.series !== "").length;

            if (this.dataLength > 0) {
              const validData = tableData
                .filter((item) => item.series !== "")
                .map((item) => {
                  return { ...item, completed: false };
                });

              if (validData.length === 0) {
                self.$ThatsNotify &&
                  self.$ThatsNotify.warning({
                    message: `Please fill up the series column.`,
                    timeOut: 10000,
                  });
                return false;
              }

              const columns = self.iterateAlphabetByNumber(
                self.exportTableOptions.columns.length
              );
              self.postedData = JSON.parse(JSON.stringify(validData));

              let chain = Promise.resolve();

              validData.forEach((element, forIndex) => {
                const row = 1 + forIndex;

                function mapElementToFormData(element) {
                  const itemAttr = self.formObject.attr;

                  const  UoMGroup = 
                        self.unitOfMeasurementGroupComputed.find(
                          (item) => item.id === element.UoMGroupEntry
                        ) || null;

                  if (UoMGroup) {
                      delete UoMGroup.UoMGroupDefinitionCollection;
                  }
                  
                  return {
                    ...self.formObject,
                    series: element.series,
                    name: element.name || null,
                    sale_price: element.sale_price || 0,
                    dimension_type: element.dimension_type || null,
                    dimension: element.dimension || null,
                    category: element.product_category_id || null,
                    product_type_id: element.product_type_id || null,
                    prType: self.normalizeProductClassification(element.product_type),
                    brand: element.brand || null,
                    area: element.area || 1,
                    collection: element.collection || null,
                    description: element.description || null,
                    website: element.website === "Yes" ? "1" : "0",
                    sell: element.sale_item === "Yes" ? "1" : "0",
                    inventory: element.inventory_item === "Yes" ? "1" : "0",
                    purchase: element.purchase_item === "Yes" ? "1" : "0",
                    allocated_quantity: 0,
                    attr: {
                      ...itemAttr,
                      old_code: element.old_code || "",
                      vendor_code: element.vendor_code || "",
                      ForeignName: element.ForeignName || null,
                      ItemCountryOrg: element.country || null,
                      barcode: element.barcode || null,
                      UoMGroupEntry:
                        self.unitOfMeasurementGroupComputed.find(
                          (item) => item.id === element.UoMGroupEntry
                        )?.AbsEntry || null,
                      DefaultUomEntry:
                        self.defaultUnitOfMeasurementComputed.find(
                          (item) => item.id === element.DefaultUomEntry
                        )?.AbsEntry || null,

                      UoMGroup: UoMGroup,
                      DefaultUom:
                        self.defaultUnitOfMeasurementComputed.find(
                          (item) => item.id === element.DefaultUomEntry
                        ) || null,
                      collection: {
                        en: element.CountryOfOriginEn,
                        ar: element.CountryOfOriginAr,
                      },
                      ProductionUomDimension: {
                        U_itm_length: element.U_itm_length,
                        U_itm_width: element.U_itm_width,
                        U_itm_height: element.U_itm_height,
                      },
                    },
                    status:
                      element.status === "Active"
                        ? "1"
                        : element.status === "Discontinued"
                        ? "0"
                        : "2",
                  };
                }

                const mappedData = mapElementToFormData(element);

                chain = chain.then(() => {
                  self.formData = { ...mappedData };

                  columns.forEach((col) => {
                    self.spreadsheet.setStyle(
                      `${col}${row}`,
                      "background-color",
                      "#fff"
                    );
                  });

                  self.spreadsheet.setValue(
                    `${columns[columns.length - 1]}${row}`,
                    "Please wait..."
                  );

                  return self
                    .storeRecord({ ...self.formData, index: row })
                    .then((response) => {
                      columns.forEach((col) => {
                        self.spreadsheet.setStyle(
                          `${col}${row}`,
                          "background-color",
                          "#00c851"
                        );
                      });
                      self.spreadsheet.setValue(
                        `${columns[columns.length - 1]}${row}`,
                        response.message
                      );
                      self.postCounter++;
                      self.$set(self.postedData[row - 1], "completed", true);
                    })
                    .catch((error) => {
                      const errors = (error.response && error.response.data && error.response.data.errors) || {};
                      let errorMessage = "";
                      errorMessage = Object.keys(errors)
                        .map((field) => `${field}: ${errors[field][0]}`)
                        .join(", ");
                      columns.forEach((col) => {
                        self.spreadsheet.setStyle(
                          `${col}${row}`,
                          "background-color",
                          "#fb3"
                        );
                      });
                      self.spreadsheet.setValue(
                        `${columns[columns.length - 1]}${row}`,
                        errorMessage
                      );
                      self.postCounter++;
                      self.$set(self.postedData[row - 1], "completed", true);
                    });
                });
              });

              chain.finally(() => {
                this.processing = false;
              });
            }
          }
        })
        .catch(() => {
          // user cancelled
        });
    },

    /* UPDATE save logic */

    submitUpdateForm() {
      const self = this;

      if (this.processing === true) {
        return false;
      }
      const tableData = this.spreadsheet.getJson();
      const tableColumnLength = this.exportTableOptions.columns.length;

      MessageBox.confirm(
        "Warning : Existing data will be overwrite. Do you want to continue?",
        "Warning",
        {
          confirmButtonText: "OK",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      ).then(() => {
        if (tableData.length > 0) {
          this.postCounter = 0;
          this.dataLength = tableData.filter((item) => item.series !== "", []).length;
          if (this.dataLength > 0) {
            const validData = tableData
              .filter((item) => item.series !== "", [])
              .map((item) => {
                return { ...item, completed: false };
              }, []);
            if (validData.length === 0) {
              self.$ThatsNotify &&
                self.$ThatsNotify.warning({
                  message: `Please fill up the series column.`,
                  timeOut: 10000,
                });
              return false;
            }

            const columns = self.iterateAlphabetByNumber(tableColumnLength);
            self.postedData = JSON.parse(JSON.stringify(validData));
            validData.forEach((element, forIndex) => {
              const row = 1 + forIndex;
              self.processing = true;

              columns.forEach((col) => {
                self.spreadsheet.setStyle(
                  `${col}${row}`,
                  "background-color",
                  "#fff",
                  true
                );
              });

              
                  const  UoMGroup = 
                        self.unitOfMeasurementGroupComputed.find(
                          (item) => item.id === element.UoMGroupEntry
                        ) || null;

                  if (UoMGroup) {
                      delete UoMGroup.UoMGroupDefinitionCollection;
                  }

              self.spreadsheet.setValue(
                `${columns[columns.length - 1]}${row}`,
                "Please wait..."
              );

             /*  UoMGroupEntry:
                        self.unitOfMeasurementGroupComputed.find(
                          (item) => item.id === element.UoMGroupEntry
                        )?.AbsEntry || null, */

              const updatePayload = { ...element,
                  area: element.area || 1,
                
                  UoMGroupEntry:
                        self.unitOfMeasurementGroupComputed.find(
                          (item) => item.id === element.UoMGroupEntry
                        )?.AbsEntry || null,

                        DefaultUomEntry:
                        self.defaultUnitOfMeasurementComputed.find(
                          (item) => item.id === element.DefaultUomEntry
                        )?.AbsEntry || null,
                        UoMGroup: UoMGroup,
                        DefaultUom:
                        self.defaultUnitOfMeasurementComputed.find(
                          (item) => item.id === element.DefaultUomEntry
                        ) || null,
                index: row };

              if (Object.prototype.hasOwnProperty.call(element, "product_category_id")) {
                updatePayload.category = element.product_category_id || null;
              }

              if (element.product_type) {
                updatePayload.prType = self.normalizeProductClassification(element.product_type);
              }

              self
                .bulkItemUpdate(updatePayload)
                .then((response) => {
                  const i = parseInt(response.index);
                  columns.forEach((col) => {
                    self.spreadsheet.setStyle(
                      `${col}${i}`,
                      "background-color",
                      "#00c851",
                      true
                    );
                  });
                  self.spreadsheet.setValue(
                    `${columns[columns.length - 1]}${i}`,
                    response.message
                  );
                  self.postCounter++;
                  self.$set(self.postedData[i - 1], "completed", true);
                })
                .catch((error) => {
                  const i = parseInt(error.response.data.index);
                  columns.forEach((col) => {
                    self.spreadsheet.setStyle(
                      `${col}${i}`,
                      "background-color",
                      "#fb3",
                      true
                    );
                  });
                  self.spreadsheet.setValue(
                    `${columns[columns.length - 1]}${i}`,
                    error.response.data.message
                  );
                  self.postCounter++;
                  self.$set(self.postedData[i - 1], "completed", true);
                });
            });
          }
        }
      });
    },

    formVar() {
      return {
        items: [],
      };
    },

    handleCheckAllChange(val) {
      this.updateCheckedOptions = val
        ? this.updateCheckboxOptions.map((i) => i.value, [])
        : [];
      this.isIndeterminate = false;
    },

    handleCheckedOptionsChange(value) {
      const checkedCount = value.length;
      this.checkAll = checkedCount === this.updateCheckboxOptions.length;
      this.isIndeterminate =
        checkedCount > 0 && checkedCount < this.updateCheckboxOptions.length;
    },

    iterateAlphabetByNumber(numColumns) {
      const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      const columns = [];

      for (let i = 0; i < numColumns; i++) {
        let label = "";
        let index = i;
        while (index >= 0) {
          label = alphabet[index % 26] + label;
          index = Math.floor(index / 26) - 1;
        }
        columns.push(label);
      }
      return columns;
    },

    clearForm() {
      this.recreateSpreadsheet();
      this.createPopupTable(this.exportTableOptions);
    },

    jspreadsheetHeaders() {
      return this.exportTableOptions.columns.map((i) => i.name, []);
    },

    recalculateDimension(instance, column, row) {
      const self = this;
      const headers = self.jspreadsheetHeaders();

      const lengthIndex = headers.indexOf("U_itm_length");
      const widthIndex = headers.indexOf("U_itm_width");
      const heightIndex = headers.indexOf("U_itm_height");
      const dimensionIndex = headers.indexOf("dimension");

      const uomGroupIndex = headers.indexOf("UoMGroupEntry");
      const defaultUomIndex = headers.indexOf("DefaultUomEntry");

      // when UoM group changes, set default from BaseUoM
      if (parseInt(column) === uomGroupIndex && defaultUomIndex !== -1) {
        const groupValue = self.spreadsheet.getValueFromCoords(
          uomGroupIndex,
          row
        );

        if (groupValue) {
          const groupRow = self.unitOfMeasurementGroups.find(
            (g) => g.Code === groupValue
          );

          let defaultUomId = "";

          if (
            groupRow &&
            groupRow.BaseUoM !== undefined &&
            groupRow.BaseUoM !== null
          ) {
            const baseUom = self.defaultUnitOfMeasurementComputed.find(
              (uom) => uom.AbsEntry === groupRow.BaseUoM
            );
            defaultUomId = baseUom ? baseUom.id : "";
          }

          if (!defaultUomId) {
            const availableUoms = self.defaultUnitOfMeasurementComputed.filter(
              (uom) => {
                return (
                  Array.isArray(uom.uomGroups) &&
                  uom.uomGroups.includes(groupValue)
                );
              }
            );
            defaultUomId =
              availableUoms.length > 0 ? availableUoms[0].id : "";
          }

          self.spreadsheet.setValueFromCoords(
            defaultUomIndex,
            row,
            defaultUomId,
            true
          );
        } else {
          self.spreadsheet.setValueFromCoords(
            defaultUomIndex,
            row,
            "",
            true
          );
        }
      }

      if (
        [lengthIndex, widthIndex, heightIndex].includes(parseInt(column))
      ) {
        const length = self.spreadsheet.getValueFromCoords(
          lengthIndex,
          row
        );
        const width = self.spreadsheet.getValueFromCoords(
          widthIndex,
          row
        );
        const height = self.spreadsheet.getValueFromCoords(
          heightIndex,
          row
        );

        const isValid = (value) =>
          value !== undefined && value !== null && value !== "";

        const dimensionArray = [];
        if (isValid(length)) dimensionArray.push(`L ${length}`);
        if (isValid(width)) dimensionArray.push(`W ${width}`);
        if (isValid(height)) dimensionArray.push(`H ${height}`);

        const dimensionString = dimensionArray.join(" x ");

        if (dimensionString.length > 0) {
          self.spreadsheet.setValueFromCoords(
            dimensionIndex,
            row,
            dimensionString + " CM",
            true
          );
        } else {
          self.spreadsheet.setValueFromCoords(
            dimensionIndex,
            row,
            "",
            true
          );
        }
      }
    },
  },

  computed: {
    ...mapGetters("InventoryItemMasterModule", {
      formRecord: "formData",
      viewLoading: "getLoading",
      postLoading: "postLoading",
      formObject: "initialFormObject",
    }),
    ...mapGetters("CountryModule", ["countries"]),
    ...mapGetters("UnitOfMeasurementModuleV2", [
      "unitOfMeasurementGroups",
      "unitOfMeasurements",
    ]),

    dialogTitle() {
      if (this.bulkAction === "create") return "Bulk Create Product";
      if (this.bulkAction === "update") return "Bulk Update Product";
      return "Bulk Action";
    },

    unitOfMeasurementGroupComputed() {
      return this.unitOfMeasurementGroups.map((item) => {
        return {
          ...item,
          name: item.Code.toUpperCase(),
          id: item.Code,
        };
      }, []);
    },

    defaultUnitOfMeasurementComputed() {
      const uomMap = this.unitOfMeasurements.reduce((acc, item) => {
        acc[item.AbsEntry] = {
          ...item,
          uomGroups: [],
        };
        return acc;
      }, {});

      this.unitOfMeasurementGroups.forEach((group) => {
        const groupCode = group.Code;
        const groupName = group.Name;

        (group.UoMGroupDefinitionCollection || [])
          .filter((def) => def.Active === "tYES")
          .forEach((def) => {
            const uom = uomMap[def.AlternateUoM];
            if (uom) {
              if (!uom.uomGroups.includes(groupCode)) {
                uom.uomGroups.push(groupCode);
              }
              if (!uom.uomGroups.includes(groupName)) {
                uom.uomGroups.push(groupName);
              }
            }
          });
      });

      return Object.values(uomMap).map((item) => {
        return {
          ...item,
          name: item.Code.toUpperCase(),  
          id: item.Code, // store code (EACH, MTR, GRM...)
        };
      });
    },

    countriesComputed() {
      return this.countries.map((item) => {
        return {
          ...item,
          name: `${item.Code} - ${item.Name.toUpperCase()}`,
          id: item.Code,
        };
      }, []);
    },

    brandComputed() {
      const brandProvider = this.brandProvider();

      return brandProvider.map((item) => {
        const name = `${item.code} - ${item.brand}`;
        return { ...item, name: name.toUpperCase(), id: item.code };
      }, []);
    },

    groupComputed() {
      const brandProviderData = this.brandProvider();
      return brandProviderData
        .filter((item) => item.parent == 0, [])
        .map((item) => {
          const name = `${item.code} - ${item.brand}`;
          return { ...item, name: name.toUpperCase(), id: item.code };
        }, []);
    },

    categoryComputed() {
      return this.categoryProvider().map((item) => {
        const name = `${item.code} - ${item.category}`;
        return { ...item, name: name.toUpperCase(), id: item.code };
      }, []);
    },

    areaComputed() {
      return this.areaProvider().map((item) => {
        const name = `${item.code} - ${item.area}`;
        return { ...item, name: name.toUpperCase(), id: item.code };
      }, []);
    },

    collectionComputed() {
      // return this.collectionProvider();

      return this.collectionProvider().map((item) => {
        const name = `${item.mdbID} - ${item.name}`;
        return { ...item, name: name.toUpperCase(), id: item.mdbID };
      }, []);
    },

    productDepartmentComputed() {
      return this.productDepartments.map((item) => ({
        ...item,
        name: `${item.id} - ${item.name}`.toUpperCase(),
        id: item.id,
      }));
    },

    productCategoryComputed() {
      return this.productCategories.map((item) => ({
        ...item,
        name: `${item.id} - ${item.name}`.toUpperCase(),
        id: item.id,
      }));
    },

    productTypeComputed() {
      return this.productTypes.map((item) => ({
        ...item,
        name: `${item.id} - ${item.name}`.toUpperCase(),
        id: item.id,
      }));
    },

    enableButton() {
      return !(
        this.brandComputed.length > 0 &&
        this.categoryComputed.length > 0
      );
    },

    isAllCompleted() {
      return this.postedData.length > 0
        ? this.postedData.every((item) => item.completed === true)
        : false;
    },

    checkedOptionsComputed() {
      if (this.bulkAction === "update") {
        return this.updateCheckedOptions.length > 0;
      }
      if (this.bulkAction === "create") {
        return this.createCheckedOptions.length > 0;
      }
      return false;
    },
  },

  watch: {
    brandComputed(v) {
      this.filteredBrands = v;
    },
    isAllCompleted(value) {
      if (value === true) {
        this.processing = false;
        this.$emit("success");
      }
    },
  },
};
</script>

<style scoped>
.custom-checkbox .custom-control-label::before {
  border-radius: 0.5rem !important;
  cursor: pointer;
}

.no-image-gallery i.far {
  font-size: 42px;
  color: #959595;
}

.mdb-time-picker .clockpicker-span-hours,
.mdb-time-picker .clockpicker-span-minutes {
  color: #4285f4 !important;
}
</style>
